import React, { createContext, useContext, useState, useEffect } from "react";
import { BASE_URL, CDN_URL } from "../config/apiconfig";

// Create context
const CartContext = createContext();

// Custom hook to use cart context
export const useCart = () => useContext(CartContext);

// Cart Provider
export const CartProvider = ({ children }) => {
  // Helper to get current user id from localStorage
  const getUserId = () => {
    try {
      const user = JSON.parse(localStorage.getItem("user"));
      return user?.id || null;
    } catch {
      return null;
    }
  };

  // Helper to get user-specific localStorage key
  const getCartKey = (userId) => (userId ? `cart_${userId}` : "cart_guest");

  // Load cart from user-specific localStorage or default to empty array
  const [cart, setCart] = useState(() => {
    const key = getCartKey(getUserId());
    const storedCart = localStorage.getItem(key);
    return storedCart ? JSON.parse(storedCart) : [];
  });

  // Safe number parsing helper
  const safeParseNumber = (value, defaultValue = 0) => {
    if (value === null || value === undefined || value === "") {
      return defaultValue;
    }
    const parsed = Number(value);
    return isNaN(parsed) ? defaultValue : parsed;
  };

  // Normalize cart item to extract base product ID and variation ID
  const normalizeCartItem = (item) => {
    let baseProductId = item.baseProductId;
    let variationId = item.variationId;

    // If baseProductId is not set but item has composite ID, extract it
    if (
      !baseProductId &&
      typeof item.id === "string" &&
      item.id.includes("_var_")
    ) {
      const [base, varId] = item.id.split("_var_");
      baseProductId = Number(base);
      variationId = Number(varId);
    } else if (!baseProductId) {
      // For regular products, baseProductId is the same as id
      baseProductId = Number(item.id);
      variationId = null;
    }

    return {
      ...item,
      baseProductId,
      variationId,
    };
  };

  // Lightweight function to fetch essential product details for cart validation
  const fetchProductDetails = async (productId) => {
    try {

      const res = await fetch(`${BASE_URL}/product/${productId}`);

      // Handle different response statuses
      if (!res.ok) {
        if (res.status === 404) {
          return { error: "Product not found", notFound: true };
        } else if (res.status === 204) {
          return { error: "Product not found", notFound: true };
        } else {
          return {
            error: `Failed to fetch product: ${res.status} ${res.statusText}`,
          };
        }
      }

      const data = await res.json();

      // Check for API response indicating product not found
      if (data?.status === "204" && data?.message === "Product not found") {
        return { error: "Product not found", notFound: true };
      }

      const p =
        data?.data && !Array.isArray(data.data)
          ? data.data
          : Array.isArray(data?.data)
            ? data.data[0]
            : data?.product;

      if (!p) {
        return { error: "Product not found", notFound: true };
      }


      // Parse main image
      let images = [];
      try {
        images = JSON.parse(p.product_images || "[]");
      } catch {}

      const mainImage =
        images.length > 0 ? `${CDN_URL}/${images[0]}` : "/placeholder.png";

      // Process customization banners
      const processCustomizationBanners = (banners) => {
        if (!Array.isArray(banners)) return [];

        return banners.map((banner) => ({
          image: banner.image ? `${CDN_URL}/${banner.image}` : null,
        })).filter(banner => banner.image);
      };

      // Process variations using consistent logic (matching ProductContext)
      const processVariations = (variations) => {
        if (!Array.isArray(variations)) return [];

        return variations.map((variation) => ({
          id: variation.id,
          productId: variation.product_id,
          title: variation.title || "",
          description: variation.description || "",
          image: variation.image ? `${CDN_URL}/${variation.image}` : null,
          color: variation.color || null,
          material: variation.material || null,
          price: safeParseNumber(variation.price, null),
          offerPrice: variation.offer_price
            ? safeParseNumber(variation.offer_price, null)
            : null,
          size: variation.size || null,
          colorCode: variation.color_code || null,
        }));
      };

      // Return only essential product details needed for cart
      const essentialDetails = {
        id: String(p.id),
        name: p.product_name ?? "",
        // Fix price mappings: max_retail_price is original price, your_price is offer price
        price: safeParseNumber(p.max_retail_price, 0), // Original price
        offerPrice: safeParseNumber(p.your_price, 0), // Offer/Sale price
        // Also include legacy fields for backward compatibility
        max_retail_price: safeParseNumber(p.max_retail_price, 0),
        your_price: safeParseNumber(p.your_price, 0),
        categoryId: String(p.category_id || ""),
        categoryName: p.category_name ?? "",
        subcategoryId: String(p.subcategory_id || ""),
        subcategoryName: p.subcategory_name ?? "",
        image: mainImage,
        // Add minimum order quantity field
        min_order_qty: safeParseNumber(p.min_order_qty, 1),
        minimumOrderQty: safeParseNumber(p.min_order_qty, 1), // Alternative field name for compatibility
        // Keep any additional fields that might be needed for compatibility
        description: p.description ?? "",
        brand: p.brand_name,
        model: p.model_name,
        material: p.material,
        color: p.color,
        colorCode: p.color_code,
        size: p.size,
        quantity: parseInt(p.quantity) || 0,
        sku: p.sku,
        averageRating: p.average_rating,
        returnPolicy: p.product_policy,
        status: p.status,
        // Add customization fields
        customisations: p.customisations || "No",
        isCustomizable: p.customisations === "Yes",
        customisedBanners: processCustomizationBanners(p.customised_banners || []),
        // Add variations using consistent processing
        variations: processVariations(p.variations || []),
        // Add bulk offers from API response
        bulk_offers: p.bulk_offers || [],
      };

      return { product: essentialDetails };
    } catch (error) {
      return { error: "Network error or API unavailable" };
    }
  };

  // Validate and refresh a single cart item using fresh product data
  const validateAndRefreshCartItem = async (cartItem) => {
    try {

      // Normalize the cart item to extract base product ID and variation ID
      const normalized = normalizeCartItem(cartItem);

      // Fetch fresh product data using base product ID
      const result = await fetchProductDetails(normalized.baseProductId);

      if (result.error) {
        return {
          isValid: false,
          cartItem,
          reason: result.notFound ? "Product no longer exists" : result.error,
        };
      }

      if (result.product) {
        let updatedCartItem = {
          ...result.product,
          quantity: cartItem.quantity,
          customization: cartItem.customization || {},
          // Preserve cart-specific fields
          originalCategoryId: cartItem.originalCategoryId,
          baseProductId: normalized.baseProductId,
          variationId: normalized.variationId,
        };

        // If this is a variation item, apply variation-specific data
        if (normalized.variationId) {
          // Use string comparison to handle both string and number IDs from API
          const variation = result.product.variations?.find(
            (v) => String(v.id) === String(normalized.variationId),
          );

          if (!variation) {
            return {
              isValid: false,
              cartItem,
              reason: "Product variation no longer exists",
            };
          }


          // Apply variation-specific data with proper field mapping and price parsing
          updatedCartItem = {
            ...updatedCartItem,

            // PRESERVE ORIGINAL CART ID (CRITICAL - don't change variation IDs)
            id: cartItem.id,

            // Keep variation metadata
            baseProductId: normalized.baseProductId,
            variationId: normalized.variationId,
            isVariation: true,
            variationTitle: variation.title,

            // Update with fresh variation data
            name: variation.title || cartItem.name,
            image: variation.image || result.product.image,

            // Use proper price parsing with fallbacks
            price: safeParseNumber(variation.price, result.product.price),
            offerPrice: safeParseNumber(
              variation.offerPrice,
              result.product.offerPrice,
            ),

            // Update other variation-specific fields
            color: variation.color || result.product.color,
            colorCode: variation.colorCode || result.product.colorCode,
            material: variation.material || result.product.material,
            size: variation.size || result.product.size,

            // Update bulk offers from fresh API data
            bulk_offers: result.product.bulk_offers || [],
          };
        } else {
          // Regular product (not a variation) - preserve original ID
          updatedCartItem.id = cartItem.id;
          updatedCartItem.isVariation = false;
          // Update bulk offers from fresh API data
          updatedCartItem.bulk_offers = result.product.bulk_offers || [];
        }


        return {
          isValid: true,
          cartItem: updatedCartItem,
          originalCartItem: cartItem,
        };
      } else {
        return {
          isValid: false,
          cartItem,
          reason: "Product data not available",
        };
      }
    } catch (error) {

      // For any errors, assume product exists but temporarily unavailable
      // Return original cart item data
      return { isValid: true, cartItem };
    }
  };

  // Use validateAndRefreshCartItem as the main validation method
  const validateCartItem = validateAndRefreshCartItem;

  // Validate all products in user's cart and update with fresh data
  const validateUserCart = async () => {
    if (cart.length === 0) {
      return { validCartItems: [], invalidCartItems: [] };
    }


    const validationPromises = cart.map((cartItem) =>
      validateAndRefreshCartItem(cartItem),
    );

    try {
      const results = await Promise.all(validationPromises);

      const validCartItems = results
        .filter((result) => result.isValid)
        .map((result) => result.cartItem);

      const invalidCartItems = results
        .filter((result) => !result.isValid)
        .map((result) => ({ ...result.cartItem, reason: result.reason }));


      if (invalidCartItems.length > 0) {
      }

      if (validCartItems.length > 0) {
      }

      return { validCartItems, invalidCartItems };
    } catch (error) {
      return { validCartItems: cart, invalidCartItems: [] };
    }
  };

  // Refresh cart with fresh product data (new enhanced method)
  const refreshCartWithFreshData = async () => {
    if (cart.length === 0) {
      return { validCartItems: [], invalidCartItems: [] };
    }


    const refreshPromises = cart.map((cartItem) =>
      validateAndRefreshCartItem(cartItem),
    );

    try {
      const results = await Promise.all(refreshPromises);

      const validCartItems = results
        .filter((result) => result.isValid)
        .map((result) => result.cartItem);

      const invalidCartItems = results
        .filter((result) => !result.isValid)
        .map((result) => ({ ...result.cartItem, reason: result.reason }));


      if (invalidCartItems.length > 0) {
      }

      // Always update cart with refreshed data to ensure variation changes are saved
      setCart(validCartItems);

      return { validCartItems, invalidCartItems };
    } catch (error) {
      return { validCartItems: cart, invalidCartItems: [] };
    }
  };

  // Update cart with fresh product data (for valid items)
  const updateCartWithFreshData = (validCartItems) => {
    if (validCartItems.length === 0) return;

    setCart(validCartItems);
  };

  // Remove invalid products from cart (silent operation - toasts handled by component)
  const removeInvalidCartItems = (invalidCartItems) => {
    if (invalidCartItems.length === 0) return;

    const invalidProductIds = invalidCartItems.map((item) => item.id);
    setCart((prev) =>
      prev.filter((item) => !invalidProductIds.includes(item.id)),
    );

    // No toast notifications here - handled by calling component to prevent duplicates
  };

  // Validate and clean user cart with fresh data (main function to call)
  const validateAndCleanCart = async () => {
    try {
      // Use refreshCartWithFreshData instead of validateUserCart to ensure variation data is properly updated
      const { validCartItems, invalidCartItems } =
        await refreshCartWithFreshData();

      return { validCartItems, invalidCartItems };
    } catch (error) {
      return { validCartItems: cart, invalidCartItems: [] };
    }
  };

  // Persist cart to user-specific localStorage whenever it changes
  useEffect(() => {
    const key = getCartKey(getUserId());
    localStorage.setItem(key, JSON.stringify(cart));
  }, [cart]);

  // Listen for login/logout to switch carts instantly
  useEffect(() => {
    const handleAuthChange = () => {
      const key = getCartKey(getUserId());
      const storedCart = localStorage.getItem(key);
      setCart(storedCart ? JSON.parse(storedCart) : []);
    };

    window.addEventListener("auth-change", handleAuthChange);
    window.addEventListener("storage", handleAuthChange);

    return () => {
      window.removeEventListener("auth-change", handleAuthChange);
      window.removeEventListener("storage", handleAuthChange);
    };
  }, []);

  // Auto-refresh cart on mount and when user changes
  useEffect(() => {
    const userId = getUserId();
    const autoRefreshCart = async () => {
      if (cart.length > 0) {
        await refreshCartWithFreshData();
      }
    };

    // Delay auto-refresh to avoid conflicts with initial load
    const timeoutId = setTimeout(autoRefreshCart, 1000);

    return () => clearTimeout(timeoutId);
  }, []); // Only run on mount to avoid unnecessary re-renders

  // Add product to cart
  const addToCart = (product, quantity = 1, customization = {}) => {
    setCart((prev) => {
      // For variations, create a composite ID, for regular products use product ID
      let itemId;
      let baseProductId;
      let variationId = null;

      if (product.isVariation) {
        baseProductId = product.baseProductId || product.id;
        variationId = product.variationId;
        itemId = `${baseProductId}_var_${variationId}`;
      } else {
        baseProductId = product.id;
        itemId = product.id;
      }

      // Check if the product with the same customization already exists
      const existingIndex = prev.findIndex(
        (item) =>
          item.id === itemId &&
          JSON.stringify(item.customization) === JSON.stringify(customization),
      );

      if (existingIndex > -1) {
        const updatedCart = [...prev];
        updatedCart[existingIndex].quantity += quantity;
        // Ensure category name is updated if it was missing
        if (!updatedCart[existingIndex].categoryName && product.categoryName) {
          updatedCart[existingIndex].categoryName = product.categoryName;
        }
        // Also update if categoryId is a name but categoryName is missing
        if (
          !updatedCart[existingIndex].categoryName &&
          typeof product.categoryId === "string" &&
          isNaN(product.categoryId)
        ) {
          updatedCart[existingIndex].categoryName = product.categoryId;
        }
        // Update customization data if available
        if (product.customisations) {
          updatedCart[existingIndex].customisations = product.customisations;
          updatedCart[existingIndex].isCustomizable = product.customisations === "Yes";
        }
        if (product.customisedBanners) {
          updatedCart[existingIndex].customisedBanners = product.customisedBanners;
        }
        // Update bulk offers if available
        if (product.bulk_offers || product.bulkOffers) {
          updatedCart[existingIndex].bulk_offers = product.bulk_offers || product.bulkOffers || [];
        }
        return updatedCart;
      }

      // Add new item - ensure all product fields are preserved
      const newItem = {
        ...product,
        id: itemId,
        baseProductId,
        variationId,
        quantity,
        customization,
        // Properly save variation metadata for cart validation
        isVariation: product.isVariation || variationId !== null,
        variationTitle: product.variationTitle || product.title || product.name,
        // Ensure category name is preserved - prioritize categoryName over categoryId
        categoryName:
          product.categoryName ||
          (typeof product.categoryId === "string" && isNaN(product.categoryId)
            ? product.categoryId
            : "Uncategorized"),
        // Keep original categoryId for reference
        originalCategoryId: product.categoryId,
        // Preserve customization data
        customisations: product.customisations || "No",
        isCustomizable: product.customisations === "Yes",
        customisedBanners: product.customisedBanners || [],
        // Preserve bulk offers
        bulk_offers: product.bulk_offers || product.bulkOffers || [],
      };
      return [...prev, newItem];
    });
  };

  // Remove item by index
  const removeFromCart = (index) => {
    setCart((prev) => prev.filter((_, i) => i !== index));
  };

  // Update quantity of an item by index with minimum order quantity validation
  const updateQuantity = (index, quantity) => {
    setCart((prev) => {
      const updatedCart = [...prev];
      const item = updatedCart[index];

      // Get minimum order quantity
      const minimumOrderQty = item?.minimumOrderQty || item?.min_order_qty || 1;

      // Ensure quantity doesn't go below minimum order quantity
      const validQuantity = Math.max(quantity, minimumOrderQty);

      updatedCart[index].quantity = validQuantity;
      return updatedCart;
    });
  };

  // Clear entire cart
  const clearCart = () => {
    setCart([]);
  };

  // Remove specific product by ID
  const removeProductById = (productId) => {
    setCart((prev) => prev.filter((item) => item.id !== productId));
  };

  return (
    <CartContext.Provider
      value={{
        cart,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        removeProductById,
        // Enhanced validation functions
        validateCartItem,
        validateUserCart,
        updateCartWithFreshData,
        removeInvalidCartItems,
        validateAndCleanCart,
        fetchProductDetails,
        // New refresh functions
        normalizeCartItem,
        validateAndRefreshCartItem,
        refreshCartWithFreshData,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};
